import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ActivityTypeClient, ActivityTypeDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const getActivityTypeClient = () => new ActivityTypeClient(API_BASE_URL, axiosInstance);

// TanStack Query Hooks

// Cached lookup for dropdowns
export const useActivityTypes = () =>
  useQuery({
    queryKey: ['activityTypes'],
    queryFn: async () => {
      const response = await getActivityTypeClient().searchActivityTypes({
        filters: { isActive: true },
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

// Non-cached search for admin tables
export const useActivityTypeSearch = () => {
  const search = async (params: any) => {
    const response = await getActivityTypeClient().searchActivityTypes(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

// Get by ID
export const useActivityTypeById = (id: string) =>
  useQuery({
    queryKey: ['activityType', id],
    queryFn: async () => {
      const response = await getActivityTypeClient().getActivityTypeById(id);
      return response?.data;
    },
    enabled: !!id,
  });

// Create mutation
export const useCreateActivityType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: ActivityTypeDTO) => {
      await getActivityTypeClient().createActivityType(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activityTypes'] });
    },
  });
};

// Update mutation
export const useUpdateActivityType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ActivityTypeDTO }) => {
      await getActivityTypeClient().updateActivityType(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activityTypes'] });
    },
  });
};

// Delete mutation
export const useDeleteActivityType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await getActivityTypeClient().deleteActivityType(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activityTypes'] });
    },
  });
};